import {
  FC,
  SyntheticEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  NewModal
} from '@platform/ui-library';
import { FormField, Select } from '@app/form_components';
import { useCustomNavigate, useServices } from '@app/hooks';
import { Option } from '@app/types';

type FormValues = {
  branchId?: string | null;
  clientId?: string | null;
  designId?: string | null;
  version?: string | null;
};

export const HomeDownloadPage: FC = () => {
  const searchDesignId = useSearchParams()[0].get('designId');
  const navigate = useCustomNavigate();
  const {
    avsService,
    errorMessageService,
    lokiService
  } = useServices();
  const [branches, setBranches] = useState<Option<string>[] | null>(null);
  const [clients, setClients] = useState<Option<string>[] | null>(null);
  const [designs, setDesigns] = useState<Option<string>[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValuesState] = useState<FormValues>({});
  const [latestVersionOption, setLatestVersionOption] = useState<Option<string> | null>(null);
  const [versions, setVersions] = useState<Option<string>[] | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    contentRef.current?.querySelector('input')?.focus();
    (async () => {
      if (!searchDesignId) {
        setClients(Option.from(
          await avsService.getClients(),
          { labelKey: 'name', valueKey: 'id' }
        ));
        return;
      }

      const designInfo = await avsService.getDesignInfo(searchDesignId);
      if (!designInfo) {
        errorMessageService.addError(`Invalid design ID ${searchDesignId}`);
        return;
      }
      const {
        client: { id: clientId },
        id: designId
      } = designInfo;
      setFormValuesState({ clientId });
      setFormValues({ designId });
    })();
  }, []);

  const setFormValues = async ({
    branchId,
    clientId,
    designId,
    version
  }: FormValues) => {
    if (version) {
      setFormValuesState((formValues) => {
        return { ...formValues, version };
      });
      return;
    }
    setLatestVersionOption(null);
    setVersions(null);
    if (branchId) {
      setFormValuesState((formValues) => {
        return {
          ...formValues,
          branchId,
          version: null
        };
      });
      const latestVersion = (await avsService.getLatestVersion(branchId))?.version;
      if (latestVersion) {
        setLatestVersionOption({ label: latestVersion, value: latestVersion });
        setFormValues({ version: latestVersion });
      }

      const options: Option<string>[] = Option.from(
        await avsService.getVersions(branchId),
        { labelKey: 'version', valueKey: 'version' }
      );
      setVersions(options);
      if (options[0]?.value && options[0].value !== latestVersion) {
        setFormValues({
          version: options[0].value
        });
      }
      return;
    }
    setBranches(null);
    if (designId) {
      setFormValuesState((formValues) => {
        return {
          ...formValues,
          designId
        };
      });
      const options: Option<string>[] = Option.from(
        await avsService.getBranches(designId),
        { labelKey: 'name', valueKey: 'id' }
      );
      setBranches(options);
      setFormValues({
        branchId: options.find(({ label }) => {
          return label === avsService.mainBranchName;
        })?.value
      });
      return;
    }
    setDesigns(null);
    if (clientId) {
      setFormValuesState({ clientId });
      const options: Option<string>[] = Option.from(
        await avsService.getDesigns(clientId),
        { labelKey: 'name', valueKey: 'id' }
      );
      setDesigns(options);
      if (options.length === 1) {
        setFormValues({ designId: options[0].value });
      }
    }
  };

  const onDownload = async (event: SyntheticEvent) => {
    event.preventDefault();
    const { branchId, version } = formValues;
    if (isLoading) {
      return;
    } else if (!branchId || !version) {
      errorMessageService.addError('Please fill in all the fields prior to submission.');
      return;
    }
    setIsLoading(true);
    try {
      const serialized = await avsService.getDesign(branchId, version);
      if (await lokiService.storeSerialized(serialized)) {
        navigate('..');
      }
    } catch (error) {
      errorMessageService.addError(error as Error);
    }
    setIsLoading(false);
  };

  return (
    <NewModal className="overflow-visible ui-box-medium" isOpen>
      <form onSubmit={onDownload}>
        <Box theme="noPadding">
          <div className="box header">
            <h2>Download Version</h2>
          </div>
          <div className="box content overflow-visible" ref={contentRef}>
            {(searchDesignId) ? null : (
              <>
                <FormField label="Client">
                  <Select
                    isLoading={!clients}
                    options={clients}
                    onChange={(clientId) => {
                      setFormValues({ clientId });
                    }}
                    value={formValues.clientId}
                  />
                </FormField>
                <FormField label="Design">
                  <Select
                    isLoading={!!formValues.clientId && !designs}
                    noOptionsMessage={() => {
                      return (formValues.clientId) ? 'No options.' : 'Select a client first.';
                    }}
                    options={designs}
                    onChange={(designId) => {
                      setFormValues({ designId });
                    }}
                    value={formValues.designId}
                  />
                </FormField>
              </>
            )}
            <div className={`flex-gap ${(searchDesignId) ? 'flex-column' : 'flex-wrap'}`}>
              <FormField
                className="flex-1"
                label="Branch"
                size="small"
              >
                <Select
                  isLoading={!!formValues.designId && !branches}
                  options={branches}
                  onChange={(branchId) => {
                    setFormValues({ branchId });
                  }}
                  value={formValues.branchId}
                />
              </FormField>
              <FormField
                className="flex-1"
                label="Version"
                size="small"
              >
                <Select
                  isLoading={!!formValues.branchId && !versions}
                  options={versions ?? ((latestVersionOption) ? [latestVersionOption] : null)}
                  onChange={(version) => {
                    setFormValues({ version: version });
                  }}
                  value={formValues.version}
                />
              </FormField>
            </div>
          </div>
          <div className="box footer no-border">
            <Button
              onClick={() => {
                navigate('..');
              }}
              theme="outline"
              type="button"
            >
              {window.l.popup.cancel}
            </Button>
            <Button theme="default">
              {(isLoading) ? 'Loading...' : 'Download'}
            </Button>
          </div>
        </Box>
      </form>
    </NewModal>
  );
};
